import clsx from 'clsx'
import type {
  EventHandler,
  KeyboardEvent,
  KeyboardEventHandler,
  MouseEvent,
  PropsWithChildren,
} from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import Cancel from '../icons/Cancel'
import { Keys } from '../utilities/keyboard'

export interface ChipsProps {
  /**
   * The color scheme of the chip
   * @default 'default'
   */
  color?: ChipColor
  /**
   * If true, the chip is be disabled
   * @default false
   */
  disabled?: boolean
  /**
   * The text content displayed in the chip
   */
  label: string
  /**
   * Callback fired when the chip is activated
   */
  onClick?: EventHandler<KeyboardEvent | MouseEvent>
  /**
   * Callback fired when the delete icon is activated.
   * If provided, a delete icon will be displayed
   */
  onDelete?: EventHandler<KeyboardEvent | MouseEvent>
  /**
   * The size of the chip
   * @default 'medium'
   */
  size?: ChipSize
  /**
   * Element or text to display at the start of the chip.
   * Can be an icon or a single letter
   */
  startAdornment?: JSX.Element | string
  /**
   * The visual style variant of the chip
   * @default 'contained'
   */
  variant?: ChipVariant
}

interface StartAdornmentProps {
  color: ChipColor
  disabled: boolean
  size: ChipSize
  variant?: ChipVariant
}

type ChipColor = 'default' | 'primary' | 'secondary'
type ChipSize = 'medium' | 'small'
type ChipVariant = 'contained' | 'outlined'

type ChipColors = {
  [key in ChipColor]: string
}

type ChipSizes = {
  [key in ChipSize]: {
    cancel: string
    root: string
    rootWithAdornment: string
    icon: string
    iconPadding: string
    cancelPadding: string
    text: string
    letter: string
  }
}

type ChipVariants = {
  default: { [key in ChipVariant]: ChipColors & { disabled: string } }
  button: { [key in ChipVariant]: ChipColors & { disabled: string } }
  focusable: { [key in ChipVariant]: ChipColors & { disabled: string } }
}

type IconColors = {
  letter: { [key in ChipColor]: string } & { disabled: string }
  cancel: { [key in ChipColor]: string }
}

const variants: ChipVariants = {
  default: {
    contained: {
      default: `border border-transparent bg-black-5 text-black-90`,
      primary: `border border-transparent bg-primary-main text-white-100`,
      secondary: `border border-transparent bg-secondary-main text-white-100`,
      disabled: `border border-transparent bg-black-15 text-black-30`,
    },
    outlined: {
      default: `border border-black-25 text-black-90 `,
      primary: `border border-primary-main text-primary-main`,
      secondary: `border border-secondary-main text-secondary-main`,
      disabled: `border border-black-15 text-black-30`,
    },
  },
  button: {
    contained: {
      default: `border border-transparent bg-black-5 text-black-90 disabled:bg-black-15 disabled:text-black-30 active:bg-black-25 focus:bg-black-15 hover:bg-black-15`,
      primary: `border border-transparent bg-primary-main text-white-100 disabled:bg-black-15 disabled:text-black-30 active:bg-primary-darker focus:bg-primary-dark hover:bg-primary-dark`,
      secondary: `border border-transparent bg-secondary-main text-white-100 disabled:bg-black-15 disabled:text-black-30 active:bg-secondary-darker focus:bg-secondary-dark hover:bg-secondary-dark`,
      disabled: `border border-transparent bg-black-15 text-black-30 cursor-not-allowed`,
    },
    outlined: {
      default: `border border-black-25 text-black-90 disabled:bg-black-15 disabled:text-black-30 active:bg-black-25 focus:bg-black-15 hover:bg-black-15`,
      primary: `border border-primary-main text-primary-main disabled:bg-black-15 disabled:text-black-30 active:bg-primary-20 focus:bg-primary-10 hover:bg-primary-10`,
      secondary: `border border-secondary-main text-secondary-main disabled:bg-black-15 disabled:text-black-30 active:bg-secondary-20 focus:bg-secondary-10 hover:bg-secondary-10`,
      disabled: `border border-black-15 text-black-30 cursor-not-allowed`,
    },
  },
  focusable: {
    contained: {
      default: `border border-transparent bg-black-5 text-black-90 active:bg-black-15 focus:bg-black-15`,
      primary: `border border-transparent bg-primary-main text-white-100 active:bg-primary-dark focus:bg-primary-dark`,
      secondary: `border border-transparent bg-secondary-main text-white-100 active:bg-secondary-dark focus:bg-secondary-dark`,
      disabled: `border border-transparent bg-black-15 text-black-30`,
    },
    outlined: {
      default: `border border-black-25 text-black-90 active:bg-black-15 focus:bg-black-15`,
      primary: `border border-primary-main text-primary-main active:bg-primary-10 focus:bg-primary-10`,
      secondary: `border border-secondary-main text-secondary-main active:bg-secondary-10 focus:bg-secondary-10`,
      disabled: `border border-black-15 text-black-30`,
    },
  },
}

const iconColors: IconColors = {
  letter: {
    default: `text-black-90 bg-black-15`,
    primary: `text-white-100 bg-primary-dark`,
    secondary: `text-white-100 bg-secondary-dark`,
    disabled: 'text-black-30 bg-black-15',
  },
  cancel: {
    default: `text-black-45 active:text-black-90 hover:text-black-60 focused:text-black-60 disabled:text-black-30 disabled:cursor-not-allowed`,
    primary: `text-primary-main hover:text-primary-dark focused:text-primary-dark active:text-primary-darker  disabled:text-black-30 disabled:cursor-not-allowed`,
    secondary: `text-secondary-main hover:text-secondary-dark focused:text-secondary-dark active:text-secondary-darker disabled:text-black-30 disabled:cursor-not-allowed`,
  },
}

const sizes: ChipSizes = {
  medium: {
    root: 'px-3 py-1',
    rootWithAdornment: 'px-3 py-1',
    icon: 'w-6 h-6',
    cancel: 'w-6 h-6',
    iconPadding: '-ml-2 mr-2',
    cancelPadding: 'ml-2 -mr-2',
    text: 'font-sans text-sm font-normal',
    letter: 'font-sans text-sm font-normal leading-6 text-center',
  },
  small: {
    root: 'px-2 py-0.5',
    rootWithAdornment: 'px-2 py-1',
    icon: 'w-4.5 h-4.5',
    cancel: 'w-4 h-4',
    iconPadding: '-ml-1 mr-1',
    cancelPadding: 'ml-1 -mr-1',
    text: 'font-sans text-xs font-normal',
    letter: 'font-sans text-xs leading-4.5 text-center',
  },
}

function Chip({
  color = 'default',
  disabled = false,
  label,
  onClick,
  onDelete,
  size = 'medium',
  startAdornment,
  variant = 'contained',
}: ChipsProps): JSX.Element {
  const intl = useIntl()

  const chipType = onClick ? 'button' : onDelete ? 'focusable' : 'default'

  const handleDelete = (event: KeyboardEvent | MouseEvent) => {
    event.stopPropagation()

    if (!disabled && onDelete) {
      onDelete(event)
    }
  }

  const onKeyDown: KeyboardEventHandler<HTMLDivElement | HTMLButtonElement> = (
    event,
  ) => {
    if (event.key === Keys.Delete || event.key === Keys.Backspace) {
      handleDelete(event)
    }

    if (event.key === Keys.Escape) {
      const button = event.target as HTMLButtonElement
      button.blur()
    }
  }

  const isButton = onClick || onDelete
  const Component = isButton ? 'button' : 'div'
  const buttonProps = isButton
    ? {
        'aria-label': label,
        disabled: disabled,
        onClick: onClick,
        onKeyDown: onKeyDown,
      }
    : {}

  return (
    <Component
      className={clsx(
        'rounded-full inline-flex items-center select-none focus:outline-none',
        startAdornment ? sizes[size].rootWithAdornment : sizes[size].root,
        disabled
          ? variants[chipType][variant].disabled
          : variants[chipType][variant][color],
        !onClick && 'cursor-default',
      )}
      {...buttonProps}
    >
      <label
        className={clsx(
          'flex items-center',
          onClick ? (disabled ? 'cursor-not-allowed' : 'cursor-pointer') : null,
        )}
      >
        {startAdornment ? (
          <StartAdornment
            color={color}
            disabled={disabled}
            size={size}
            variant={variant}
          >
            {startAdornment}
          </StartAdornment>
        ) : null}
        <span className={clsx('flex', sizes[size].text)}>{label}</span>
      </label>
      {onDelete ? (
        <div
          aria-label={intl.formatMessage({ id: 'Chip.remove' })}
          className={clsx(
            'focus:outline-none',
            sizes[size].cancel,
            sizes[size].cancelPadding,
            disabled
              ? 'cursor-not-allowed'
              : variant === 'contained'
              ? iconColors.cancel.default
              : iconColors.cancel[color],
          )}
          role="button"
          tabIndex={-1}
          {...(disabled
            ? {}
            : {
                onClick: handleDelete,
              })}
        >
          <Cancel />
        </div>
      ) : null}
    </Component>
  )
}

function StartAdornment({
  children,
  color,
  disabled,
  size,
  variant,
}: PropsWithChildren<StartAdornmentProps>): JSX.Element {
  const startAdornmentString = typeof children === 'string'

  return (
    <div
      className={clsx(
        'flex relative',
        sizes[size].icon,
        sizes[size].iconPadding,
        startAdornmentString &&
          clsx(
            'rounded-full',
            disabled
              ? iconColors.letter.disabled
              : variant === 'contained'
              ? 'bg-black-30'
              : iconColors.letter[color],
          ),
      )}
    >
      <div
        className={clsx(
          'absolute inset-x-0',
          startAdornmentString
            ? sizes[size].letter
            : 'overflow-hidden rounded-full',
          sizes[size].icon,
        )}
      >
        {children}
      </div>
    </div>
  )
}

export default Chip
